import { prepareLazyComponentsForSource } from '@Pages/pageRuntime/components/registry';
import { prepareSdkDndForSource } from '@Pages/pageRuntime/dnd/lazyDnd';
import { prepareLazyHooksForSource } from '@Pages/pageRuntime/hooks/lazyFormHooks';
import {
  explicitRuntimeLoad,
  renderRuntimeLoad,
  type RuntimeLoad
} from '@Pages/pageRuntime/sdkMemberLoaders';

/**
 * Runtime that source factories can require while they are evaluated.
 *
 * Components and hooks expose lazy facades that can wait under React Suspense
 * when the authored component renders. `Sdk.Dnd` is a proxy, so a top-level
 * property read needs its implementation before the factory runs.
 */
export function evaluationRuntimeLoads(
  sources: Array<string | null | undefined>
): Promise<unknown> | null {
  return prepareSdkDndForSource(sources, explicitRuntimeLoad);
}

function runtimeLoads(
  sources: Array<string | null | undefined>,
  load: RuntimeLoad
): Promise<unknown> | null {
  const loads = [
    prepareSdkDndForSource(sources, load),
    prepareLazyHooksForSource(sources, load),
    prepareLazyComponentsForSource(sources, load)
  ].filter((pending): pending is Promise<unknown> => pending !== null);

  return loads.length > 0 ? Promise.all(loads) : null;
}

/**
 * The optional runtime chunks these sources reference and have not loaded yet,
 * or null when everything they need is already resident. The result rejects
 * when a chunk fails to load.
 */
export function optionalRuntimeLoads(
  sources: Array<string | null | undefined>
): Promise<unknown> | null {
  return runtimeLoads(sources, explicitRuntimeLoad);
}

/**
 * Suspends until the optional runtime these sources reference is resident. A
 * chunk that failed to load stops the wait, and the member that needs it
 * reports the failure when it renders.
 */
export function prepareOptionalRuntime(sources: Array<string | null | undefined>): void {
  const load = runtimeLoads(sources, renderRuntimeLoad);

  if (load !== null) {
    throw load;
  }
}
